import Enums from "./enums";
import { getLegacyCommercialEquivalencyGroup } from "./commercialEquivalencyUtils";
import { isBooleanTrue, isListNotEmpty, isNumberNotEmpty, isObjectNotEmpty, isStringNotEmpty } from "./objectUtils";

const toEnumKeyMap = (enumObj) => Object.freeze(enumObj.keys().reduce((acc, key) => {
    acc[key] = key;
    return acc;
}, {}));

export const COUPON_DISCOUNT_TARGET_TYPES = toEnumKeyMap(Enums.CouponDiscountTargetTypes);

export const COUPON_DISCOUNT_VARIANT_SCOPES = toEnumKeyMap(Enums.CouponDiscountVariantScopes);

const getNormalizedTargetType = (discount = {}) => {
    if (discount?.targetType === COUPON_DISCOUNT_TARGET_TYPES.COMMERCIAL_EQUIVALENCY_GROUP || isStringNotEmpty(discount?.commercialEquivalencyGroupId)) {
        return COUPON_DISCOUNT_TARGET_TYPES.COMMERCIAL_EQUIVALENCY_GROUP;
    }

    return COUPON_DISCOUNT_TARGET_TYPES.PRODUCT;
};

const getNormalizedVariantScope = (discount = {}) => {
    if (discount?.variantScope === COUPON_DISCOUNT_VARIANT_SCOPES.SPECIFIC) {
        return COUPON_DISCOUNT_VARIANT_SCOPES.SPECIFIC;
    }

    if (discount?.variantScope === COUPON_DISCOUNT_VARIANT_SCOPES.ALL) {
        return COUPON_DISCOUNT_VARIANT_SCOPES.ALL;
    }

    return isListNotEmpty(discount?.variantIds)
        ? COUPON_DISCOUNT_VARIANT_SCOPES.SPECIFIC
        : COUPON_DISCOUNT_VARIANT_SCOPES.ALL;
};

export const normalizeCouponDiscountRow = (discount = {}) => {
    const targetType = getNormalizedTargetType(discount);
    const variantScope = targetType === COUPON_DISCOUNT_TARGET_TYPES.COMMERCIAL_EQUIVALENCY_GROUP
        ? COUPON_DISCOUNT_VARIANT_SCOPES.ALL
        : getNormalizedVariantScope(discount);

    return {
        targetType,
        productId: targetType === COUPON_DISCOUNT_TARGET_TYPES.PRODUCT ? (discount?.productId ?? null) : null,
        commercialEquivalencyGroupId: targetType === COUPON_DISCOUNT_TARGET_TYPES.COMMERCIAL_EQUIVALENCY_GROUP ? (discount?.commercialEquivalencyGroupId ?? null) : null,
        variantScope,
        variantIds: variantScope === COUPON_DISCOUNT_VARIANT_SCOPES.SPECIFIC && isListNotEmpty(discount?.variantIds) ? [...new Set(discount.variantIds.filter(Boolean))] : [],
        discountType: isStringNotEmpty(discount?.discountType) ? discount.discountType : 'FLAT_AMOUNT',
        discountAmount: isNumberNotEmpty(discount?.discountAmount) ? Number(discount.discountAmount) : 0,
    };
};

export const normalizeCouponDiscountRows = (discounts = []) => {
    if (!isListNotEmpty(discounts)) {
        return [];
    }

    return discounts.map((discount) => normalizeCouponDiscountRow(discount));
};

export const validateCouponDiscountRows = (discounts = []) => {
    normalizeCouponDiscountRows(discounts).forEach((discount, index) => {
        const rowNumber = index + 1;
        if (!Enums.CouponDiscountTargetTypes.includesKey(discount.targetType)) {
            throw new Error(`Coupon discount row ${rowNumber} has an invalid target type.`);
        }
        if (!Enums.CouponDiscountVariantScopes.includesKey(discount.variantScope)) {
            throw new Error(`Coupon discount row ${rowNumber} has an invalid variant scope.`);
        }
        if (!Enums.DiscountTypes.includesKey(discount.discountType)) {
            throw new Error(`Coupon discount row ${rowNumber} has an invalid discount type.`);
        }
        if (!Number.isFinite(Number(discount.discountAmount)) || Number(discount.discountAmount) <= 0) {
            throw new Error(`Coupon discount row ${rowNumber} must have a discount amount greater than zero.`);
        }
        if (discount.targetType === COUPON_DISCOUNT_TARGET_TYPES.PRODUCT && !isStringNotEmpty(discount.productId)) {
            throw new Error(`Coupon discount row ${rowNumber} requires a product.`);
        }
        if (discount.targetType === COUPON_DISCOUNT_TARGET_TYPES.COMMERCIAL_EQUIVALENCY_GROUP && !isStringNotEmpty(discount.commercialEquivalencyGroupId)) {
            throw new Error(`Coupon discount row ${rowNumber} requires a commercial equivalency group.`);
        }
        if (discount.variantScope === COUPON_DISCOUNT_VARIANT_SCOPES.SPECIFIC && !isListNotEmpty(discount.variantIds)) {
            throw new Error(`Coupon discount row ${rowNumber} requires at least one variant when variant scope is Specific Variants.`);
        }
    });

    return true;
};

const getCommercialEquivalencyGroupIdForItem = ({ product = null, productId = null, productSku = null }) => {
    if (isStringNotEmpty(product?.commercialEquivalencyGroupId)) {
        return product.commercialEquivalencyGroupId;
    }

    if (isStringNotEmpty(product?.sku)) {
        return getLegacyCommercialEquivalencyGroup(product.sku)?.id ?? null;
    }

    if (isStringNotEmpty(productSku)) {
        return getLegacyCommercialEquivalencyGroup(productSku)?.id ?? null;
    }

    if (isStringNotEmpty(productId) && isStringNotEmpty(product?.id) && product.id === productId && isStringNotEmpty(product?.commercialEquivalencyGroupId)) {
        return product.commercialEquivalencyGroupId;
    }

    return null;
};

const matchesTarget = (discount, { product = null, productId = null, productSku = null }) => {
    if (discount.targetType === COUPON_DISCOUNT_TARGET_TYPES.PRODUCT) {
        return isStringNotEmpty(discount.productId) && discount.productId === (product?.id ?? productId ?? null);
    }

    if (discount.targetType === COUPON_DISCOUNT_TARGET_TYPES.COMMERCIAL_EQUIVALENCY_GROUP) {
        const commercialEquivalencyGroupId = getCommercialEquivalencyGroupIdForItem({ product, productId, productSku });
        return isStringNotEmpty(discount.commercialEquivalencyGroupId) && discount.commercialEquivalencyGroupId === commercialEquivalencyGroupId;
    }

    return false;
};

const matchesVariant = (discount, variantId = null, allowTargetOnlyFallback = false) => {
    if (discount.variantScope !== COUPON_DISCOUNT_VARIANT_SCOPES.SPECIFIC) {
        return true;
    }

    if (allowTargetOnlyFallback && !isStringNotEmpty(variantId)) {
        return true;
    }

    return isStringNotEmpty(variantId) && discount.variantIds.includes(variantId);
};

const getRowPriority = (discount) => {
    const targetPriority = discount.targetType === COUPON_DISCOUNT_TARGET_TYPES.PRODUCT ? 20 : 10;
    const variantPriority = discount.variantScope === COUPON_DISCOUNT_VARIANT_SCOPES.SPECIFIC ? 5 : 0;
    return targetPriority + variantPriority;
};

export const getMatchingCouponDiscountRow = (discounts = [], itemContext = {}) => {
    const normalizedRows = normalizeCouponDiscountRows(discounts);
    const matches = normalizedRows
        .filter((discount) => matchesTarget(discount, itemContext) && matchesVariant(discount, itemContext?.variantId ?? null, isBooleanTrue(itemContext?.allowTargetOnlyFallback)))
        .sort((a, b) => getRowPriority(b) - getRowPriority(a));

    return matches[0] ?? null;
};

export const shouldCouponDiscountRowApply = (discounts = [], itemContext = {}) => {
    return isObjectNotEmpty(getMatchingCouponDiscountRow(discounts, itemContext));
};

export const shouldCouponDiscountApplyToAllVariants = (discount = {}, productVariants = []) => {
    const normalizedDiscount = normalizeCouponDiscountRow(discount);
    if (normalizedDiscount.targetType !== COUPON_DISCOUNT_TARGET_TYPES.PRODUCT) {
        return true;
    }

    if (!isListNotEmpty(productVariants)) {
        return normalizedDiscount.variantScope === COUPON_DISCOUNT_VARIANT_SCOPES.ALL;
    }

    if (normalizedDiscount.variantScope === COUPON_DISCOUNT_VARIANT_SCOPES.ALL) {
        return true;
    }

    const enabledVariantIds = productVariants
        .filter((variant) => isBooleanTrue(variant?.enabled))
        .map((variant) => variant.id)
        .filter(Boolean);

    if (!isListNotEmpty(enabledVariantIds)) {
        return false;
    }

    return enabledVariantIds.every((variantId) => normalizedDiscount.variantIds.includes(variantId));
};
